import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { AlertCircle, GripVertical, Pencil, Plus, Sparkles, Trash2 } from 'lucide-react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmModal,
  EmptyState,
  Skeleton,
  Thumbnail,
} from '@kardume/ui';
import { extractErrorMessage, formatCurrency, merchantFeaturedItemApi } from '@kardume/services';
import { FEATURED_ITEM_ERROR_MESSAGES, FEATURED_ITEM_MAX_COUNT } from '@kardume/types';
import type { MerchantFeaturedItemDto, MerchantFeaturedItemRequestDto } from '@kardume/types';
import { useNotification } from '@kardume/hooks';
import FeaturedItemFormModal from './FeaturedItemFormModal';

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  width: 100%;

  h3 {
    margin: 0;
  }
`;

const Count = styled.span<{ $full: boolean }>`
  font-size: 0.78rem;
  font-weight: 600;
  color: ${(p) => (p.$full ? p.theme.warning : p.theme.textMuted)};
`;

const Hint = styled.p`
  margin: 0 0 1rem;
  font-size: 0.78rem;
  color: ${(p) => p.theme.textMuted};
`;

/**
 * O aviso do teto. Fica visível junto do botão desabilitado, e não só na hora
 * do clique: um botão apagado sem explicação é lido como "quebrou".
 */
const CapNote = styled.p`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0 0 1rem;
  font-size: 0.78rem;
  color: ${(p) => p.theme.warning};
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const Row = styled.li<{ $dragging: boolean; $busy: boolean }>`
  display: grid;
  grid-template-columns: auto auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.85rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surface};
  opacity: ${(p) => (p.$dragging ? 0.5 : p.$busy ? 0.65 : 1)};
  transition: opacity 0.15s, border-color 0.15s;

  &:hover {
    border-color: ${(p) => p.theme.primary};
  }

  @media (max-width: 560px) {
    grid-template-columns: auto auto minmax(0, 1fr);
    /* As ações caem para a linha de baixo, alinhadas à direita. */
    > *:last-child {
      grid-column: 1 / -1;
      justify-self: end;
    }
  }
`;

/**
 * A alça é um `<button>`, e não uma `<div>` com ícone, porque arrastar não é
 * alcançável por teclado: aqui ela é focável e responde a ↑/↓, que é o mesmo
 * reordenar pelo mesmo endpoint. Sem isso a ordenação seria uma função só de
 * quem usa mouse.
 */
const Handle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.3rem;
  border: none;
  border-radius: ${(p) => p.theme.radius};
  background: transparent;
  color: ${(p) => p.theme.textMuted};
  cursor: grab;

  &:disabled {
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.primary};
    outline-offset: 1px;
  }
  &:active:not(:disabled) {
    cursor: grabbing;
  }
`;

const Info = styled.div`
  min-width: 0;
`;

const Name = styled.strong`
  display: block;
  font-size: 0.9rem;
  color: ${(p) => p.theme.textPrimary};
  overflow-wrap: anywhere;
`;

const Description = styled.span`
  display: block;
  margin-top: 0.15rem;
  font-size: 0.78rem;
  color: ${(p) => p.theme.textMuted};
  overflow-wrap: anywhere;
`;

const Price = styled.span`
  display: block;
  margin-top: 0.25rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
`;

/**
 * "Sem preço" no painel - e NÃO no site nem no app.
 *
 * Lá o item sem preço não desenha linha nenhuma (§2 do spec 033). Aqui a
 * ausência precisa ser dita: o lojista tem de conseguir distinguir "não pus
 * preço neste" de "esqueci de preencher", e uma linha em branco não distingue.
 * O que não pode aparecer, em lugar nenhum, é "R$ 0,00".
 */
const NoPrice = styled.span`
  display: block;
  margin-top: 0.25rem;
  font-size: 0.82rem;
  font-style: italic;
  color: ${(p) => p.theme.textMuted};
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const IconButton = styled.button<{ $danger?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.4rem;
  border: none;
  border-radius: ${(p) => p.theme.radius};
  background: transparent;
  color: ${(p) => (p.$danger ? p.theme.error : p.theme.textSecondary)};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${(p) => p.theme.lightGray};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 2rem 1.5rem;
  text-align: center;
  color: ${(p) => p.theme.error};

  p {
    margin: 0;
    font-size: 0.85rem;
  }
`;

const SkeletonStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

/** Move um item de posição, devolvendo uma lista nova. */
const moveItem = <T,>(list: T[], from: number, to: number): T[] => {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return list;
  const next = list.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

const sameOrder = (a: MerchantFeaturedItemDto[], b: MerchantFeaturedItemDto[]): boolean =>
  a.length === b.length && a.every((item, index) => item.id === b[index].id);

/**
 * Gestão dos produtos e serviços em destaque da loja (card #218, §6 do spec 033).
 *
 * ## Por que ele salva sozinho, dentro de um formulário que tem Salvar
 *
 * O resto da aba "Dados da loja" é um `PUT /v1/merchant/profile` só, disparado
 * pelo Salvar do rodapé. Os destaques têm CRUD próprio
 * (`/v1/merchant/featured-items`) e gravam na hora - cada item é um recurso,
 * não um campo do perfil. Amarrá-los ao Salvar do perfil obrigaria a tela a
 * guardar criações, edições, remoções e a ordem nova em memória e a despejar
 * tudo numa sequência de chamadas no fim, sem transação: a metade que falhasse
 * deixaria a tela e o banco discordando, que é exatamente o que o `PUT /order`
 * de lista inteira existe para evitar.
 *
 * O preço dessa escolha é uma confusão possível ("cadê meu Salvar?"), e ela é
 * paga com a frase logo abaixo do título - não com um segundo botão Salvar.
 *
 * ## Por que todo botão daqui é `type="button"`
 *
 * O painel é renderizado DENTRO do `<form>` do `ProfilePage`, e o padrão de um
 * `<button>` sem `type` é `submit`. Sem o `type="button"`, arrastar, remover ou
 * até abrir o modal salvaria o perfil inteiro de tabela.
 */
export const FeaturedItemsPanel: React.FC = () => {
  const { showNotification } = useNotification();

  const [items, setItems] = useState<MerchantFeaturedItemDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<MerchantFeaturedItemDto | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  const [removing, setRemoving] = useState<MerchantFeaturedItemDto | null>(null);
  const [removingBusy, setRemovingBusy] = useState(false);

  const [reordering, setReordering] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  /**
   * A última ordem que o core confirmou. É para cá que a lista volta quando o
   * `PUT /order` falha - sem esse espelho, um erro de rede deixaria a tela
   * mostrando uma ordem que não existe no banco.
   */
  const persisted = useRef<MerchantFeaturedItemDto[]>([]);

  const adopt = useCallback((list: MerchantFeaturedItemDto[]) => {
    setItems(list);
    persisted.current = list;
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    return merchantFeaturedItemApi
      .list()
      .then(adopt)
      .catch((err) =>
        setLoadError(extractErrorMessage(err, 'Não foi possível carregar os itens em destaque.'))
      )
      .finally(() => setLoading(false));
  }, [adopt]);

  useEffect(() => {
    load();
  }, [load]);

  const full = items.length >= FEATURED_ITEM_MAX_COUNT;
  const busy = saving || removingBusy || reordering;

  const fail = (err: unknown, fallback: string) =>
    showNotification(extractErrorMessage(err, fallback, FEATURED_ITEM_ERROR_MESSAGES), 'error');

  /* --------------------------------------------------------- criar/editar */

  const openCreate = () => {
    // O teto é avisado antes de o lojista preencher o formulário inteiro para
    // levar um 400 no fim. O core continua sendo quem recusa de verdade - ver
    // o `catch` de `handleSubmit`, que trata o caso de a contagem daqui estar
    // velha (outra aba aberta, por exemplo).
    if (full) {
      showNotification(
        `Você já tem ${FEATURED_ITEM_MAX_COUNT} itens em destaque - o máximo. Remova um para incluir outro.`,
        'info'
      );
      return;
    }
    setEditing(undefined);
    setFormOpen(true);
  };

  const openEdit = (item: MerchantFeaturedItemDto) => {
    setEditing(item);
    setFormOpen(true);
  };

  const handleSubmit = async (payload: MerchantFeaturedItemRequestDto) => {
    setSaving(true);
    try {
      if (editing) {
        const updated = await merchantFeaturedItemApi.update(editing.id, payload);
        adopt(items.map((item) => (item.id === updated.id ? updated : item)));
        showNotification('Item atualizado.', 'success');
      } else {
        // O core devolve o item já com a `position` do fim da lista: a tela
        // acrescenta o que ele mandou, não o que ela chutou.
        const created = await merchantFeaturedItemApi.create(payload);
        adopt([...items, created]);
        showNotification('Item adicionado aos destaques.', 'success');
      }
      setFormOpen(false);
      setEditing(undefined);
    } catch (err) {
      fail(err, 'Não foi possível salvar o item.');
      // O teto recusado pelo servidor significa que a contagem daqui estava
      // velha. Reler é o que devolve a tela à verdade - e é o que faz o botão
      // "Adicionar" apagar sozinho na sequência.
      if (!editing) load();
    } finally {
      setSaving(false);
    }
  };

  /* --------------------------------------------------------------- remover */

  const handleRemove = async () => {
    if (!removing) return;
    setRemovingBusy(true);
    try {
      await merchantFeaturedItemApi.remove(removing.id);
      // O core compacta as posições sozinho; a tela não as recalcula, e a
      // ordem que ela desenha é a ordem do array - nunca o campo `position`.
      adopt(items.filter((item) => item.id !== removing.id));
      showNotification('Item removido dos destaques.', 'success');
      setRemoving(null);
    } catch (err) {
      fail(err, 'Não foi possível remover o item.');
    } finally {
      setRemovingBusy(false);
    }
  };

  /* ------------------------------------------------------------- reordenar */

  /**
   * Grava a ordem que está na tela mandando **a lista inteira de ids**.
   *
   * Não é um "sobe/desce" por chamada (§4 do spec 033): arrastar um item do fim
   * para o começo seria N chamadas, e cada uma é uma chance de a tela e o banco
   * discordarem no meio.
   */
  const commitOrder = async (next: MerchantFeaturedItemDto[]) => {
    if (sameOrder(next, persisted.current)) return;
    const before = persisted.current;
    setReordering(true);
    try {
      adopt(await merchantFeaturedItemApi.reorder(next.map((item) => item.id)));
    } catch (err) {
      setItems(before);
      fail(err, 'Não foi possível salvar a nova ordem.');
    } finally {
      setReordering(false);
    }
  };

  const handleDragStart = (index: number) => (event: React.DragEvent<HTMLLIElement>) => {
    if (busy) return;
    setDragIndex(index);
    event.dataTransfer.effectAllowed = 'move';
    // O Firefox só inicia o arrasto se houver dado no dataTransfer.
    event.dataTransfer.setData('text/plain', items[index].id);
  };

  const handleDragOver = (index: number) => (event: React.DragEvent<HTMLLIElement>) => {
    if (dragIndex === null) return;
    // Sem o preventDefault o navegador recusa o alvo e o `drop` nunca acontece.
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (dragIndex === index) return;
    // Reordena enquanto arrasta: a linha acompanha o cursor, em vez de pular
    // para o lugar novo só quando o botão do mouse é solto.
    setItems((prev) => moveItem(prev, dragIndex, index));
    setDragIndex(index);
  };

  const handleDragEnd = () => {
    if (dragIndex === null) return;
    setDragIndex(null);
    commitOrder(items);
  };

  /** ↑/↓ na alça: o mesmo reordenar, para quem não usa mouse. */
  const handleHandleKeyDown = (index: number) => (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    const to = event.key === 'ArrowUp' ? index - 1 : index + 1;
    if (to < 0 || to >= items.length) return;
    event.preventDefault();
    const next = moveItem(items, index, to);
    setItems(next);
    commitOrder(next);
  };

  /* ----------------------------------------------------------------- render */

  const body = () => {
    if (loading) {
      return (
        <SkeletonStack>
          <Skeleton height="56px" />
          <Skeleton height="56px" />
        </SkeletonStack>
      );
    }

    if (loadError) {
      return (
        <ErrorWrap>
          <AlertCircle size={26} />
          <p>{loadError}</p>
          <Button type="button" variant="outline" onClick={load}>
            Tentar novamente
          </Button>
        </ErrorWrap>
      );
    }

    if (items.length === 0) {
      return (
        <EmptyState
          icon={<Sparkles size={30} />}
          title="Nenhum item em destaque ainda"
          description="Mostre o que a sua loja vende: o prato, o corte, o serviço que faz alguém atravessar a cidade. Loja sem item cadastrado não desenha o bloco na vitrine."
          action={
            <Button type="button" onClick={openCreate}>
              <Plus size={16} /> Adicionar item
            </Button>
          }
        />
      );
    }

    return (
      <List>
        {items.map((item, index) => (
          <Row
            key={item.id}
            draggable={!busy}
            onDragStart={handleDragStart(index)}
            onDragOver={handleDragOver(index)}
            onDrop={(event: React.DragEvent<HTMLLIElement>) => event.preventDefault()}
            onDragEnd={handleDragEnd}
            $dragging={dragIndex === index}
            $busy={busy}
          >
            <Handle
              type="button"
              disabled={busy || items.length < 2}
              onKeyDown={handleHandleKeyDown(index)}
              aria-label={`Reordenar ${item.name} - posição ${index + 1} de ${items.length}. Use as setas para cima e para baixo.`}
              title="Arraste para reordenar (ou use ↑ e ↓)"
            >
              <GripVertical size={16} />
            </Handle>
            <Thumbnail src={item.imageUrl ?? undefined} alt={item.name} width={72} />
            <Info>
              <Name>{item.name}</Name>
              {item.description && <Description>{item.description}</Description>}
              {/*
                `item.price != null`, e nunca um `||` nem um `?? 0`: preço zero
                não existe neste recurso (o core recusa), mas é o `null` que
                manda aqui - e é ele que troca "R$ 0,00" por "Sem preço fixo".
              */}
              {item.price != null ? (
                <Price>{formatCurrency(item.price)}</Price>
              ) : (
                <NoPrice>Sem preço fixo</NoPrice>
              )}
            </Info>
            <Actions>
              <IconButton
                type="button"
                onClick={() => openEdit(item)}
                disabled={busy}
                aria-label={`Editar ${item.name}`}
                title="Editar"
              >
                <Pencil size={16} />
              </IconButton>
              <IconButton
                type="button"
                $danger
                onClick={() => setRemoving(item)}
                disabled={busy}
                aria-label={`Remover ${item.name}`}
                title="Remover"
              >
                <Trash2 size={16} />
              </IconButton>
            </Actions>
          </Row>
        ))}
      </List>
    );
  };

  return (
    <Card>
      <CardHeader>
        <HeaderRow>
          <h3>Produtos e serviços em destaque</h3>
          <Count $full={full}>
            {items.length} de {FEATURED_ITEM_MAX_COUNT}
          </Count>
        </HeaderRow>
      </CardHeader>
      <CardBody>
        <Hint>
          A vitrine da sua loja: o que ela vende, e não só o que ela é. Arraste para mudar a ordem -
          é nela que os itens aparecem no site e no aplicativo. Estas alterações são salvas na hora,
          sem depender do Salvar do rodapé.
        </Hint>

        {full && (
          <CapNote>
            <AlertCircle size={14} />
            Você chegou ao máximo de {FEATURED_ITEM_MAX_COUNT} itens. Remova um para incluir outro -
            destaque demais deixa de ser destaque.
          </CapNote>
        )}

        {body()}

        {items.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <Button type="button" variant="outline" onClick={openCreate} disabled={full || busy}>
              <Plus size={16} /> Adicionar item
            </Button>
          </div>
        )}
      </CardBody>

      <FeaturedItemFormModal
        isOpen={formOpen}
        item={editing}
        saving={saving}
        onSubmit={handleSubmit}
        onClose={() => {
          if (saving) return;
          setFormOpen(false);
          setEditing(undefined);
        }}
      />

      <ConfirmModal
        isOpen={!!removing}
        danger
        title="Remover item em destaque"
        message={
          removing
            ? `"${removing.name}" sai da vitrine da sua loja no site e no aplicativo. Os outros itens sobem uma posição.`
            : ''
        }
        confirmLabel="Remover"
        loading={removingBusy}
        onConfirm={handleRemove}
        onClose={() => {
          if (removingBusy) return;
          setRemoving(null);
        }}
      />
    </Card>
  );
};

export default FeaturedItemsPanel;
